<script setup>
import { ref, watch } from 'vue'
import { useEntitiesStore } from '@/stores/useEntitiesStore'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  expendeduria: { type: Object, default: null }
})

const emit = defineEmits(['close', 'save'])
const store = useEntitiesStore()

const activeTab = ref('ficha')
const formData = ref({})
const isSaving = ref(false)
const errorMessage = ref('')

// Sincronizar formulario al cambiar la expendeduria seleccionada
watch(() => props.expendeduria, (newVal) => {
  if (newVal) {
    formData.value = { ...newVal }
  }
}, { immediate: true })

const close = () => {
  errorMessage.value = ''
  emit('close')
}

const handleSave = async () => {
  if (!props.expendeduria?.id) return

  isSaving.value = true
  errorMessage.value = ''

  try {
    // 1. Llamada a la Store para actualizar en la API
    console.log('Datos a guardar:', formData.value)
    await store.updateExpendeduria(formData.value)
    
    // 2. Cierra el drawer tras el éxito
    close()
  } catch (error) {
    errorMessage.value = 'Error al guardar los cambios. Inténtalo de nuevo.'
    console.error('Error al guardar:', error)
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div :class="{ 'drawer-open': isOpen }">
    <!-- Overlay -->
    <div class="overlay" @click="close"></div>

    <!-- Drawer Lateral -->
    <aside class="drawer">
      <!-- Cabecera -->
      <div class="drawer-header" v-if="expendeduria">
        <div>
          <h3 class="m-0 fs-5 fw-bold text-dark">{{ expendeduria.name }}</h3>
          <span class="text-muted small">
            ID: #{{ expendeduria.id }} | Módems: {{ expendeduria.sent_modems }} Env / {{ expendeduria.working_modems }} Com
          </span>
        </div>
        <button class="btn-close" @click="close"></button>
      </div>

      <!-- Pestañas -->
      <div class="tabs-bar">
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'ficha' }" 
          @click="activeTab = 'ficha'"
        >
          Ficha General
        </button>
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'envios' }" 
          @click="activeTab = 'envios'"
        >
          Envíos 
        </button>
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'incidencias' }" 
          @click="activeTab = 'incidencias'"
        >
          Incidencias
        </button>
      </div>

      <!-- Cuerpo del Drawer -->
      <div class="drawer-body" v-if="expendeduria">
        <!-- Error al guardar -->
        <div v-if="errorMessage" class="alert alert-danger py-2 small mb-3">
          ⚠️ {{ errorMessage }}
        </div>

        <!-- Alerta Dinámica -->
        <div v-if="expendeduria.has_danger_alert" class="alert alert-danger py-2 small mb-3">
          🚨 <strong>Alerta:</strong> Ha pasado 1 mes desde la primera venta y el SEPA sigue pendiente.
        </div>

        <!-- Tab: Ficha General -->
        <form v-if="activeTab === 'ficha'" id="expendeduriaForm" @submit.prevent="handleSave">
        <!-- Campos de pedido -->
          <div class="row g-3 mb-3">
            <div class="col-6">
              <label class="form-label small fw-bold">Fecha Primer Pedido</label>
              <input type="date" class="form-control form-control-sm" disabled v-model="formData.date_first_order" />
            </div>
            <div class="col-6">
              <label class="form-label small fw-bold">Fecha Primera Llegada (Envio)</label>
              <input type="date" class="form-control form-control-sm" disabled v-model="formData.date_first_arrive" />
            </div>
          </div>
          
          <!-- Campos de SEPA -->
          <div class="row g-3 mb-3">
            <div class="col-6">
              <label class="form-label small fw-bold">Estado SEPA</label>
              <select class="form-select form-select-sm" disabled v-model="formData.sepa_status">
                <option value="PENDING">Pendiente</option>
                <option value="SIGNED">Firmado</option>
                <option value="CANCELED">Cancelado</option>
              </select>
            </div>
            <div class="col-6">
              <label class="form-label small fw-bold">Fecha SEPA</label>
              <input type="date" class="form-control form-control-sm" disabled v-model="formData.sepa_date" />
            </div>
          </div>

          <!-- Campos de Telemetría -->
          <div class="row g-3 mb-3">
            <div class="col-6">
              <label class="form-label small fw-bold">Telemetría Previa</label>
              <input type="text" class="form-control form-control-sm" disabled v-model="formData.previous_telemetry_label" />
            </div>

          </div>

          <!-- Campos de Formación -->
          <div class="row g-3 mb-3">
            <div class="col-6">
              <label class="form-label small fw-bold">Formación</label>
              <select class="form-select form-select-sm" v-model="formData.lesson_status">
                <option value="PENDING">Pendiente</option>
                <option value="SCHEDULED">Agendado</option>
                <option value="COMPLETED">Completado</option>
                <option value="USER_NOT_DISPOSED">Usuario no disponible</option>
              </select>
            </div>
            <div class="col-6">
              <label class="form-label small fw-bold">Fecha Formación</label>
              <input type="date" class="form-control form-control-sm" v-model="formData.lesson_date" />
            </div>
          </div>
          <!-- Ahora añadiremos 2 checks o selecionables que dirán si se ha hecho el seguimiento (llamada a los 7 y 20 días), pero serán 2 seleccionables independientes que se pueden marcar -->
          <div class="row g-3 mb-3">
            <div class="col-6">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="formData.follow_up_7_days" id="follow_up_7_days">
                <label class="form-check-label small fw-bold" for="follow_up_7_days">
                  Seguimiento 7 días
                </label>
              </div>
            </div>
            <div class="col-6">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="formData.follow_up_20_days" id="follow_up_20_days">
                <label class="form-check-label small fw-bold" for="follow_up_20_days">
                  Seguimiento 20 días
                </label>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label small fw-bold">Observaciones</label>
            <textarea class="form-control form-control-sm" rows="3" v-model="formData.notes"></textarea>
          </div>
        </form>

        <!-- Otros Tabs -->
        <div v-else-if="activeTab === 'envios'" class="text-muted small py-3">
          Historial de Envíos asociados.
        </div>
        <div v-else-if="activeTab === 'incidencias'" class="text-muted small py-3">
          Listado de incidencias registradas.
        </div>
      </div>

      <!-- Footer -->
      <div v-if="activeTab === 'ficha'" class="drawer-footer">
        <button class="btn btn-sm btn-outline-secondary" @click="close" :disabled="isSaving">Cancelar</button>
        <button class="btn btn-sm btn-dark" type="submit" form="expendeduriaForm" :disabled="isSaving">
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
          Guardar Cambios
        </button>
      </div>
    </aside>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(2px);
  opacity: 0; visibility: hidden; transition: all 0.3s ease; z-index: 1040;
}

.drawer {
  position: fixed; top: 0; right: -650px; width: 650px; max-width: 100vw; height: 100vh;
  background: #fff; box-shadow: -10px 0 25px rgba(0,0,0,0.15);
  transition: right 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 1050; display: flex; flex-direction: column;
}

.drawer-open .overlay { opacity: 1; visibility: visible; }
.drawer-open .drawer { right: 0; }

.drawer-header {
  padding: 16px 20px; border-bottom: 1px solid #e2e8f0; background: #f8fafc;
  display: flex; justify-content: space-between; align-items: flex-start;
}

.tabs-bar {
  display: flex; border-bottom: 1px solid #e2e8f0; background: #f8fafc; padding: 0 20px; gap: 8px;
}

.tab-btn {
  padding: 10px 14px; border: none; background: none; font-size: 0.85rem; font-weight: 600;
  color: #64748b; cursor: pointer; border-bottom: 2px solid transparent;
}

.tab-btn.active { color: #0f172a; border-bottom-color: #0f172a; }

.drawer-body { padding: 20px; overflow-y: auto; flex: 1; }

.drawer-footer {
  padding: 12px 20px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; background: #fff;
}
</style>